import { z } from 'zod';

/**
 * Provedor de IA de cada docente (chave própria; o custo é de quem configura).
 * - ANTHROPIC: API da Anthropic (modelos Claude).
 * - OPENAI_COMPATIBLE: qualquer endpoint compatível com a API de chat da OpenAI
 *   (OpenAI, OpenRouter, Azure, Ollama/vLLM próprios…), informado pela URL base.
 */
export const AI_PROVIDERS = ['ANTHROPIC', 'OPENAI_COMPATIBLE'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
  ANTHROPIC: 'Claude (Anthropic)',
  OPENAI_COMPATIBLE: 'Compatível com OpenAI',
};

/** Sugestões exibidas na interface (o campo aceita qualquer modelo do provedor). */
export const AI_MODEL_SUGGESTIONS: Record<AiProvider, string[]> = {
  ANTHROPIC: ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5'],
  OPENAI_COMPATIBLE: [],
};

/** URLs base de exemplo para endpoints compatíveis com OpenAI. */
export const AI_BASE_URL_PRESETS: { label: string; url: string }[] = [
  { label: 'OpenAI', url: 'https://api.openai.com/v1' },
  { label: 'OpenRouter', url: 'https://openrouter.ai/api/v1' },
];

export const aiSettingsSchema = z
  .object({
    provider: z.enum(AI_PROVIDERS),
    model: z
      .string()
      .trim()
      .min(1, 'Informe o modelo')
      .max(120, 'Nome de modelo muito longo')
      .regex(/^[\w.:/@+-]+$/, 'Use o identificador do modelo (sem espaços)'),
    /** Obrigatória para OPENAI_COMPATIBLE; ignorada para ANTHROPIC. */
    baseUrl: z
      .url({ protocol: /^https?$/, message: 'URL inválida (use http ou https)' })
      .max(300)
      .nullable()
      .optional(),
    /** Nova chave. Ausente: mantém a atual. null: remove a chave salva. */
    apiKey: z.string().trim().min(8, 'Chave muito curta').max(500).nullable().optional(),
  })
  .refine((v) => v.provider !== 'OPENAI_COMPATIBLE' || Boolean(v.baseUrl), { message: 'Informe a URL base do provedor', path: ['baseUrl'] });
export type AiSettingsInput = z.infer<typeof aiSettingsSchema>;

/** Configuração devolvida ao navegador: a chave nunca sai do servidor (apenas os 4 últimos caracteres). */
export interface AiSettingsDto {
  provider: AiProvider;
  model: string;
  baseUrl: string | null;
  hasApiKey: boolean;
  apiKeyHint: string | null;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  updatedAt: string;
}
